import React, { useEffect, useState } from 'react'
import './footer.css'
import video from '../../Assets/sail.mp4'
import { useNavigate, useLocation } from 'react-router-dom'
import { useTranslation, Trans } from 'react-i18next'
import {FiSend, FiArrowUp} from 'react-icons/fi'
import {config} from "../../config";

import image from '../../Assets/logo.png'

import Aos from 'aos'
import 'aos/dist/aos.css'

const Footer = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  // { key } for translated statuses, { text } for raw server messages —
  // keeps the status readable when the user switches language afterwards
  const [submissionStatus, setSubmissionStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [submissionSuccess, setSubmissionSuccess] = useState(false);

  useEffect(() => {
    Aos.init({
      duration: 2000,
      once: true
    })
  }, [])

  // Smooth scroll function — from another page, go home first
  const scrollToSection = (elementId) => {
    if (location.pathname !== '/') {
      navigate('/', { state: { scrollTo: elementId } });
      return;
    }

    const element = document.getElementById(elementId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmissionStatus(null);
    setIsLoading(true);
    setSubmissionSuccess(false);

    // Basic validation
    if (!email.trim() || !message.trim()) {
      setSubmissionStatus({ key: 'footer.fillAllFields' });
      setIsLoading(false);
      return;
    }

    try {
      const response = await fetch(`${config.baseUrl}/contact_us`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          message,
        }),
      });

      const responseData = await response.json();

      if (response.ok && responseData.success) {
        setSubmissionStatus({ key: 'footer.success' });
        setSubmissionSuccess(true);
        // Clear form
        setEmail('');
        setMessage('');
      } else {
        setSubmissionStatus(
            responseData.message
                ? { text: responseData.message }
                : { key: 'footer.sendError' }
        );
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmissionStatus({ key: 'footer.connectionError' });
    } finally {
      setIsLoading(false);
    }
  }

  return (
      <section className='footer'>
        <div className="container">
          <div className="footCard">

            {/* --- Video background --- */}
            <div className="videoDiv">
              <video
                  src={video}
                  muted
                  autoPlay
                  loop
                  playsInline
                  preload="auto"
                  disablePictureInPicture
                  controls={false}
                  controlsList="nodownload nofullscreen noplaybackrate"
              ></video>
            </div>

            {/* --- Top row: brand · nav · back-to-top --- */}
            <div className="footTop">
              <div className="footBrand" onClick={() => scrollToSection('home')}>
                <img src={image} alt="Madagasikara Ekôlôjia"/>
                <div className="footBrandText">
                  <h1>Madagasikara Ekôlôjia</h1>
                  <p>{t('footer.tagline')}</p>
                </div>
              </div>

              <button
                  className="toTop"
                  onClick={() => scrollToSection('home')}
                  aria-label={t('footer.backToTop')}
              >
                <FiArrowUp/>
              </button>
            </div>

            {/* --- Middle: contact info + form --- */}
            <div className="footGrid">
              <div className="footInfo">
                <h2>{t('footer.stayInTouch')}</h2>
                <p>{t('footer.p1')}</p>
                <p>{t('footer.p2')}</p>
                <p>
                  <Trans
                      i18nKey="footer.p3"
                      components={{ email: <b className="email"/> }}
                  />
                </p>

                <div className="footContacts">
                  <div className="footContact">
                    <img
                        src="https://cdn-ilcckdj.nitrocdn.com/QlUnTVTrZOOqlaTqMQtiukCnxjpNglhu/assets/images/optimized/rev-262d696/www.yas.mg/wp-content/uploads/2024/02/jem_logo-1.svg"
                        className="contactImage" alt="Yas"
                    />
                    <a href="tel:+261346134210" className="contactLink">
                      +261 34 61 342 10
                    </a>
                  </div>
                  <div className="footContact">
                    <img
                        src="https://upload.wikimedia.org/wikipedia/commons/thumb/c/c8/Orange_logo.svg/330px-Orange_logo.svg.png"
                        className="contactImage" alt="Orange"
                    />
                    <a href="tel:+261324000999" className="contactLink">
                      +261 32 40 009 99
                    </a>
                  </div>
                </div>

                <small className="footAddress">
                  {t('footer.address')}
                </small>
              </div>

              <form onSubmit={handleSubmit} className="footForm">
                <h3>{t('footer.formTitle')}</h3>
                <input
                    placeholder={t('footer.emailPlaceholder')}
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    disabled={isLoading}
                />
                <textarea
                    placeholder={t('footer.messagePlaceholder')}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    required
                    disabled={isLoading}
                />
                {submissionStatus && (
                    <div style={{
                      color: submissionSuccess
                          ? 'hsl(147, 92%, 63%)'
                          : '#FF9C8A'
                    }}>
                      {submissionStatus.key ? t(submissionStatus.key) : submissionStatus.text}
                    </div>
                )}
                <button
                    className='btn sendMessage'
                    type='submit'
                    disabled={isLoading}
                >
                  {isLoading ? (
                      <>
                        {t('footer.sending')}
                        <div className="spinner-container">
                          <div className="spinner"></div>
                        </div>
                      </>
                  ) : (
                      <>
                        {t('footer.send')} <FiSend className="icon"/>
                      </>
                  )}
                </button>
              </form>
            </div>

            {/* --- Bottom bar --- */}
            <div className="footBottom">
              <small>
                {t('footer.rights')}
              </small>
            </div>

          </div>
        </div>
      </section>
  )
}

export default Footer
